import { getZoneForPosition } from '../data/deliveryZones'

/**
 * Approximate centroid for every ZIP code in and around the service area.
 *
 * These are NOT used for routing or final billing — they exist so that when a
 * street address can't be geocoded (rural highway addresses, new plats, typos
 * in the unit number), we can still tell the customer roughly what delivery
 * will cost instead of showing them nothing.
 *
 * Deliberately resolved through `getZoneForPosition` rather than a hand-built
 * ZIP-to-zone table: the zone polygons stay the single source of truth, so
 * editing a zone boundary automatically updates every ZIP estimate too.
 */
const ZIP_CENTROIDS: Record<string, [number, number]> = {
  // Seattle
  '98101': [47.611, -122.334],
  '98102': [47.634, -122.322],
  '98103': [47.673, -122.342],
  '98104': [47.602, -122.329],
  '98105': [47.663, -122.301],
  '98106': [47.539, -122.353],
  '98107': [47.668, -122.376],
  '98108': [47.541, -122.313],
  '98109': [47.632, -122.347],
  '98112': [47.635, -122.293],
  '98115': [47.685, -122.301],
  '98116': [47.577, -122.397],
  '98117': [47.688, -122.376],
  '98118': [47.542, -122.271],
  '98119': [47.639, -122.368],
  '98122': [47.614, -122.301],
  '98125': [47.717, -122.301],
  '98126': [47.548, -122.373],
  '98133': [47.74, -122.343],
  '98134': [47.578, -122.334],
  '98136': [47.539, -122.388],
  '98144': [47.59, -122.297],
  '98146': [47.5, -122.357],
  '98148': [47.443, -122.325],
  '98155': [47.756, -122.302],
  '98158': [47.443, -122.302],
  '98166': [47.451, -122.352],
  '98168': [47.485, -122.298],
  '98177': [47.746, -122.371],
  '98178': [47.499, -122.244],
  '98188': [47.446, -122.277],
  '98198': [47.393, -122.313],
  '98199': [47.65, -122.4],
  // Eastside
  '98004': [47.618, -122.203],
  '98005': [47.615, -122.166],
  '98006': [47.559, -122.152],
  '98007': [47.615, -122.14],
  '98008': [47.606, -122.117],
  '98033': [47.678, -122.194],
  '98034': [47.717, -122.208],
  '98040': [47.573, -122.226],
  '98052': [47.68, -122.121],
  '98053': [47.665, -122.038],
  '98027': [47.52, -122.02],
  '98029': [47.545, -122.01],
  '98074': [47.615, -122.048],
  '98075': [47.586, -122.028],
  '98072': [47.76, -122.14],
  '98077': [47.75, -122.07],
  '98011': [47.756, -122.204],
  '98021': [47.786, -122.199],
  '98028': [47.757, -122.244],
  '98019': [47.742, -121.985],
  '98014': [47.648, -121.912],
  '98065': [47.528, -121.825],
  '98045': [47.494, -121.786],
  // South King
  '98055': [47.462, -122.208],
  '98056': [47.512, -122.187],
  '98057': [47.472, -122.222],
  '98058': [47.447, -122.147],
  '98059': [47.492, -122.13],
  '98030': [47.377, -122.198],
  '98031': [47.395, -122.198],
  '98032': [47.393, -122.256],
  '98042': [47.36, -122.128],
  '98038': [47.383, -122.038],
  '98010': [47.312, -122.003],
  '98022': [47.204, -121.99],
  '98001': [47.31, -122.267],
  '98002': [47.308, -122.216],
  '98092': [47.284, -122.156],
  '98003': [47.312, -122.313],
  '98023': [47.31, -122.36],
  '98047': [47.265, -122.25],
  '98070': [47.412, -122.462],
  // Pierce
  '98402': [47.251, -122.437],
  '98403': [47.263, -122.454],
  '98404': [47.213, -122.4],
  '98405': [47.246, -122.474],
  '98406': [47.262, -122.512],
  '98407': [47.288, -122.512],
  '98408': [47.203, -122.442],
  '98409': [47.22, -122.478],
  '98418': [47.226, -122.448],
  '98421': [47.259, -122.41],
  '98422': [47.296, -122.397],
  '98424': [47.235, -122.353],
  '98443': [47.226, -122.377],
  '98444': [47.155, -122.437],
  '98445': [47.147, -122.415],
  '98465': [47.253, -122.535],
  '98466': [47.23, -122.531],
  '98467': [47.222, -122.56],
  '98498': [47.163, -122.523],
  '98499': [47.17, -122.492],
  '98439': [47.13, -122.545],
  '98371': [47.199, -122.302],
  '98372': [47.213, -122.267],
  '98373': [47.15, -122.319],
  '98374': [47.145, -122.27],
  '98375': [47.113, -122.309],
  '98387': [47.096, -122.396],
  '98390': [47.213, -122.226],
  '98391': [47.18, -122.18],
  '98354': [47.248, -122.313],
  '98338': [47.052, -122.263],
  '98360': [47.098, -122.204],
  '98321': [47.163, -122.027],
  '98328': [46.867, -122.267],
  '98332': [47.383, -122.612],
  '98335': [47.3, -122.6],
  // Snohomish
  '98012': [47.845, -122.204],
  '98036': [47.815, -122.31],
  '98037': [47.84, -122.28],
  '98043': [47.792, -122.31],
  '98201': [47.986, -122.209],
  '98203': [47.94, -122.23],
  '98204': [47.895, -122.245],
  '98208': [47.895, -122.2],
  '98270': [48.052, -122.152],
  '98271': [48.1, -122.2],
  '98272': [47.855, -121.972],
  '98290': [47.912, -122.07],
  '98296': [47.837, -122.1],
  // Thurston / Kitsap edges
  '98501': [47.03, -122.88],
  '98502': [47.06, -122.95],
  '98503': [47.03, -122.79],
  '98506': [47.075, -122.87],
  '98513': [47.0, -122.74],
  '98516': [47.075, -122.78],
  '98597': [46.94, -122.605],
  '98310': [47.578, -122.622],
  '98311': [47.63, -122.63],
  '98312': [47.56, -122.72],
  '98366': [47.535, -122.63],
  '98367': [47.48, -122.68],
  '98370': [47.735, -122.64],
  '98383': [47.65, -122.7],
}

/**
 * Pull a 5-digit ZIP out of a free-typed address string. Anchored on a word
 * boundary and restricted to Washington's 98xxx/99xxx range so a house number
 * or a phone fragment can't be mistaken for a ZIP.
 */
export function extractZip(input: string): string | null {
  const match = input.match(/\b(9[89]\d{3})\b/)
  return match ? match[1] : null
}

export type ZipEstimate = {
  zip: string
  zone: number
  fee: number
}

/**
 * Best-effort delivery estimate from a ZIP code alone. Returns null when the
 * ZIP is unknown or its centroid falls outside every delivery zone — in those
 * cases we'd rather show nothing than a number dispatch can't honor.
 */
export function getZoneForZip(zip: string): ZipEstimate | null {
  const centroid = ZIP_CENTROIDS[zip]
  if (!centroid) return null
  const info = getZoneForPosition(centroid[0], centroid[1])
  if (!info.zone) return null
  return {
    zip,
    zone: info.zone,
    fee: info.fee,
  }
}

/** Convenience: estimate straight from a typed address string. */
export function estimateZoneFromAddress(address: string): ZipEstimate | null {
  const zip = extractZip(address)
  return zip ? getZoneForZip(zip) : null
}

/** Every city we deliver to, lowercase, for the specificity test below. */
const KNOWN_CITIES = [
  'seattle',
  'bellevue',
  'kirkland',
  'redmond',
  'sammamish',
  'issaquah',
  'mercer island',
  'medina',
  'newcastle',
  'renton',
  'kent',
  'auburn',
  'federal way',
  'burien',
  'tukwila',
  'seatac',
  'sea tac',
  'des moines',
  'maple valley',
  'covington',
  'black diamond',
  'snoqualmie',
  'north bend',
  'enumclaw',
  'tacoma',
  'puyallup',
  'sumner',
  'bonney lake',
  'gig harbor',
  'lakewood',
  'edgewood',
  'milton',
  'fife',
  'orting',
  'buckley',
  'graham',
  'spanaway',
  'university place',
  'woodinville',
  'bothell',
  'shoreline',
  'kenmore',
  'duvall',
  'carnation',
  'ravensdale',
  'kirkland',
]

/**
 * Is this typed address specific enough to STOP suggesting completions for?
 *
 * A house number and a street name are enough for a geocoder to return *a*
 * coordinate — "10700 SE 260th" happily resolves — but not enough to know
 * which of them the customer meant. The same street name and number recur
 * across the Puget Sound, and the resolved point decides both the delivery
 * zone fee and the sales-tax jurisdiction, so a lucky guess is not good
 * enough to treat as confirmed.
 *
 * A ZIP or a recognizable city name is what disambiguates it. Until one of
 * those is present, autocomplete must stay available — the customer still has
 * a decision left to make, even though the field already resolved.
 */
export function addressLooksSpecific(address: string): boolean {
  const q = address.trim().toLowerCase()
  if (!q) return false
  // A Washington ZIP pins it exactly.
  if (extractZip(q)) return true
  // Otherwise require a city we actually serve. Checked as a whole word so
  // "Kentwood Place" doesn't read as "Kent".
  return KNOWN_CITIES.some((city) =>
    new RegExp(`(^|[^a-z])${city.replace(/ /g, '\\s+')}([^a-z]|$)`).test(q),
  )
}
